<script setup>
const props = defineProps({
  data: Object,
  isStatic: Boolean
})
let items = [];
if (props.isStatic) {
  items = props.data.image.data;
} 
if (!props.isStatic) {
  items = props.data.carousel.image.data;
}
let isVideo = false;
if (items.length == 1 && items[0].attributes.mime.includes('video/')) {
    isVideo = true;
}
const carouselSliderId = 'carousel-' + props.data.id;
const videoRef = ref(null);
const shouldLoadVideo = ref(false);
const getItemAlt = (item) =>
  resolveMediaAlt(item);
let videoObserver = null;

const loadVideoSource = () => {
  if (shouldLoadVideo?.value) return;
  shouldLoadVideo.value = true;
};

onMounted(() => {
  const indicators = document.querySelectorAll(`#${carouselSliderId} .carousel-indicators .rounded-circle`);
  if (indicators.length > 1) {
    // CW-853: initialize Bootstrap touch/swipe without requiring a manual dot click first.
    indicators[0].click();
  }

  if (isVideo && videoRef.value) {
    videoObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        loadVideoSource();
        videoObserver?.disconnect();
        videoObserver = null;
      }
    }, { rootMargin: '300px 0px' });

    videoObserver.observe(videoRef?.value);
  }
});

onUnmounted(() => {
  videoObserver?.disconnect();
  videoObserver = null;
});
</script>
<template>
  <div class="block carousel">
    <div class="container">
      <div class="row">
        <div class="col">
        <template v-if="isVideo">
          <video ref="videoRef" playsinline autoplay muted loop preload="none" id="bgvideo" class="w-100 h-100">
            <source v-if="shouldLoadVideo" :src="items[0].attributes.url" :type="items[0].attributes.mime">
          </video>
        </template>
      
      <div v-else :id="carouselSliderId" class="carousel slide" data-bs-interval="false">
        <ol class="carousel-indicators">
          <template v-if="items.length > 1">
            <li v-for="(item, index) in items" 
              :key="index" 
              :data-bs-target="'#' + carouselSliderId" 
              :data-bs-slide-to="index" 
              :class="['rounded-circle', 'my-0', 'mx-2', index === 0 ? 'active' : '']">
            </li>
          </template>
        </ol>
        <div class="carousel-inner">
          <div v-for="(item, index) in items" :key="`item-${data.id}-${index}`" class="carousel-item" :class="index === 0 ? 'active' : ''" >
            <NuxtImg 
              :src="item.attributes.url" 
              :alt="getItemAlt(item)" 
              loading="lazy"
              decoding="async"
              format="webp"
              aria-hidden="true"
              class="w-100"
            />                
          </div>
        </div>
        <a v-if="items.length > 1" class="carousel-control-next d-none d-md-flex" href="#" role="button" data-bs-slide="next" :data-bs-target="'#' + carouselSliderId">
          <span class="circle"><i class="icon-chevron-right"></i></span>
        </a>
        <a v-if="items.length > 1" class="carousel-control-prev d-none d-md-flex" href="#" role="button" data-bs-slide="prev" :data-bs-target="'#' + carouselSliderId">
          <span class="circle"><i class="icon-chevron-left"></i></span>
        </a>
      </div>
    </div>
  </div></div></div>
</template>
<style lang="scss" scoped>
.carousel {
  cursor: default;
  .carousel-item {
    img {
      border-radius: 24px;
    }
  }
  .icon-chevron-right, .icon-chevron-left {
    font-size: 1.75rem;
    line-height: inherit;
  }
  .circle {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    line-height: 42px;
    background-color: black;
    opacity: 0.4;
    @include media-breakpoint-up(lg) {
        width: 52px;
        height: 52px;
        line-height: 52px;
    }
    &:hover{
        opacity: 0.6;
    }
  }
  .carousel-indicators {
    li {
      cursor: pointer;
      opacity: 0.4;
      overflow: hidden;
      transition: all $animation-duration-medium;
      width: 10px;
      height: 10px;
      background-color: white;
    }
    .active {
      width: 12px;
      height: 12px;
      opacity: 1;
      background-color: $color-id-green;
    }
  }
  .carousel-control-next{
    text-decoration: none;
  }
  .carousel-control-prev{
    text-decoration: none;
  }
  a {
    text-decoration: none;
  }
}
</style>